import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Lock, Save, Key, Settings, Camera, X, Smartphone, Download, Bell, Plus, Trash2, RefreshCw } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import EmailConfig from './EmailConfig';
import { authService } from '../services/authService';
import api from '../services/api';
import toast from 'react-hot-toast';
import { POSITIONS } from '../constants/company';
import { useQuery, useMutation, useQueryClient } from 'react-query';

const Profile = () => {
  const { user, updateProfile, changePassword, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isDownloadingApk, setIsDownloadingApk] = useState(false);
  const fileInputRef = useRef(null);

  const { register: registerProfile, handleSubmit: handleProfileSubmit, formState: { errors: profileErrors }, reset: resetProfile } = useForm({
    defaultValues: {
      username: user?.username || '',
      email: user?.email || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
      position: user?.position || '',
      hireDate: user?.hireDate ? new Date(user.hireDate).toISOString().split('T')[0] : '',
      notes: user?.notes || ''
    }
  });

  // Aktualizuj wartości domyślne gdy user się zmieni
  React.useEffect(() => {
    if (user) {
      resetProfile({
        username: user.username || '',
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        position: user.position || '',
        hireDate: user.hireDate ? new Date(user.hireDate).toISOString().split('T')[0] : '',
        notes: user.notes || ''
      });
    }
  }, [user, resetProfile]);

  const { register: registerPassword, handleSubmit: handlePasswordSubmit, reset: resetPassword, formState: { errors: passwordErrors } } = useForm();

  const onProfileSubmit = async (data) => {
    setIsUpdating(true);
    try {
      await updateProfile(data);
    } finally {
      setIsUpdating(false);
    }
  };

  const onPasswordSubmit = async (data) => {
    setIsChangingPassword(true);
    try {
      await changePassword(data.currentPassword, data.newPassword);
      resetPassword();
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Sprawdź typ pliku
    if (!file.type.startsWith('image/')) {
      toast.error('Tylko pliki obrazów są dozwolone');
      return;
    }

    // Sprawdź rozmiar pliku (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Plik jest za duży. Maksymalny rozmiar to 5MB');
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const response = await authService.uploadAvatar(file);
      await refreshUser();
      toast.success('Awatar został przesłany pomyślnie');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Błąd podczas przesyłania awatara');
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAvatarDelete = async () => {
    if (!window.confirm('Czy na pewno chcesz usunąć awatar?')) {
      return;
    }

    setIsUploadingAvatar(true);
    try {
      await authService.deleteAvatar();
      await refreshUser();
      toast.success('Awatar został usunięty');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania awatara');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleDownloadApk = async () => {
    setIsDownloadingApk(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Musisz być zalogowany aby pobrać aplikację');
        setIsDownloadingApk(false);
        return;
      }

      // Użyj axios z responseType: 'blob' dla pobierania pliku binarnego
      // Token jest automatycznie dodawany przez interceptor w api.js
      const response = await api.get('/android/download', {
        responseType: 'blob'
      });

      // Utwórz blob z odpowiedzi
      const blob = new Blob([response.data], { type: 'application/vnd.android.package-archive' });
      
      // Utwórz URL dla blob i pobierz plik
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'farm-connect.apk';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Aplikacja została pobrana pomyślnie!');
    } catch (error) {
      console.error('Błąd pobierania aplikacji:', error);
      
      // Obsługa błędów z axios
      if (error.response) {
        if (error.response.status === 401) {
          toast.error('Sesja wygasła. Zaloguj się ponownie.');
          window.location.href = '/login';
          return;
        }
        if (error.response.status === 404) {
          toast.error('Aplikacja Android nie jest jeszcze dostępna. Skontaktuj się z administratorem.');
          return;
        }
        // Spróbuj odczytać komunikat błędu z odpowiedzi JSON
        if (error.response.data instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result);
              toast.error(errorData.message || 'Błąd pobierania aplikacji');
            } catch {
              toast.error('Wystąpił błąd podczas pobierania aplikacji');
            }
          };
          reader.readAsText(error.response.data);
          return;
        }
        toast.error(error.response.data?.message || 'Wystąpił błąd podczas pobierania aplikacji');
      } else if (error.request) {
        toast.error('Nie można połączyć się z serwerem. Sprawdź połączenie internetowe.');
      } else {
        toast.error(error.message || 'Wystąpił błąd podczas pobierania aplikacji');
      }
    } finally {
      setIsDownloadingApk(false);
    }
  };

  const getAvatarUrl = () => {
    if (user?.avatar) {
      // Usuń /api z początku URL jeśli jest, ponieważ statyczne pliki są dostępne bezpośrednio
      const avatarPath = user.avatar.startsWith('/uploads/') ? user.avatar : `/uploads${user.avatar}`;
      // Użyj bezpośrednio farm.my-it.pl bez /api dla statycznych plików
      const baseUrl = process.env.REACT_APP_API_URL 
        ? process.env.REACT_APP_API_URL.replace('/api', '')
        : 'https://farm.my-it.pl';
      return `${baseUrl}${avatarPath}`;
    }
    return null;
  };

  const tabs = [
    { id: 'profile', name: 'Profil', icon: User },
    { id: 'password', name: 'Hasło', icon: Lock },
    { id: 'email', name: 'Email', icon: Settings, superAdminOnly: true },
    { id: 'notifications', name: 'Powiadomienia', icon: Bell, superAdminOnly: true },
    { id: 'android', name: 'Aplikacja Android', icon: Smartphone }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              // Sprawdź czy użytkownik ma dostęp do zakładki
              if (tab.superAdminOnly && user?.role !== 'super admin') {
                return null;
              }

              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group flex items-center px-3 py-2 text-sm font-medium rounded-md border-l-4 w-full text-left`}
                >
                  <Icon
                    className={`${
                      activeTab === tab.id ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                    } mr-3 flex-shrink-0 h-5 w-5`}
                  />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {activeTab === 'profile' && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Informacje o profilu
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Zaktualizuj swoje dane osobowe
                </p>
              </div>
              <div className="card-body">
                {/* Sekcja awatara */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Awatar
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      {getAvatarUrl() ? (
                        <img
                          src={getAvatarUrl()}
                          alt="Awatar"
                          className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                          <User className="h-10 w-10 text-gray-400" />
                        </div>
                      )}
                      {isUploadingAvatar && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                          <LoadingSpinner size="sm" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col space-y-2">
                      <label className="cursor-pointer">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          className="hidden"
                          disabled={isUploadingAvatar}
                        />
                        <span className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                          <Camera className="h-4 w-4 mr-2" />
                          {isUploadingAvatar ? 'Przesyłanie...' : 'Wybierz zdjęcie'}
                        </span>
                      </label>
                      {getAvatarUrl() && (
                        <button
                          type="button"
                          onClick={handleAvatarDelete}
                          disabled={isUploadingAvatar}
                          className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 transition-colors"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Usuń awatar
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Maksymalny rozmiar: 5MB. Akceptowane formaty: JPG, PNG, GIF
                  </p>
                </div>

                <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                        Nazwa użytkownika
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          {...registerProfile('username', {
                            required: 'Nazwa użytkownika jest wymagana',
                            minLength: {
                              value: 3,
                              message: 'Nazwa użytkownika musi mieć co najmniej 3 znaki'
                            },
                            maxLength: {
                              value: 30,
                              message: 'Nazwa użytkownika nie może przekraczać 30 znaków'
                            },
                            pattern: {
                              value: /^[a-zA-Z0-9_.-]+$/,
                              message: 'Nazwa użytkownika może zawierać tylko litery, cyfry, podkreślenia, kropki i myślniki'
                            }
                          })}
                          type="text"
                          className={`block w-full pl-10 pr-3 py-2 border ${
                            profileErrors.username ? 'border-red-300' : 'border-gray-300'
                          } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                          placeholder="Nazwa użytkownika"
                        />
                      </div>
                      {profileErrors.username && (
                        <p className="mt-1 text-sm text-red-600">{profileErrors.username.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Adres email
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          {...registerProfile('email', {
                            required: 'Email jest wymagany',
                            pattern: {
                              value: /^\S+@\S+$/i,
                              message: 'Nieprawidłowy format email'
                            }
                          })}
                          type="email"
                          className={`block w-full pl-10 pr-3 py-2 border ${
                            profileErrors.email ? 'border-red-300' : 'border-gray-300'
                          } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                          placeholder="Adres email"
                        />
                      </div>
                      {profileErrors.email && (
                        <p className="mt-1 text-sm text-red-600">{profileErrors.email.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Dane pracownika */}
                  <div className="border-t pt-6 mt-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Dane pracownika (opcjonalne)</h4>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                          Imię
                        </label>
                        <input
                          {...registerProfile('firstName', {
                            maxLength: {
                              value: 50,
                              message: 'Imię nie może przekraczać 50 znaków'
                            }
                          })}
                          type="text"
                          className={`block w-full px-3 py-2 border ${
                            profileErrors.firstName ? 'border-red-300' : 'border-gray-300'
                          } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                          placeholder="Jan"
                        />
                        {profileErrors.firstName && (
                          <p className="mt-1 text-sm text-red-600">{profileErrors.firstName.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                          Nazwisko
                        </label>
                        <input
                          {...registerProfile('lastName', {
                            maxLength: {
                              value: 50,
                              message: 'Nazwisko nie może przekraczać 50 znaków'
                            }
                          })}
                          type="text"
                          className={`block w-full px-3 py-2 border ${
                            profileErrors.lastName ? 'border-red-300' : 'border-gray-300'
                          } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                          placeholder="Kowalski"
                        />
                        {profileErrors.lastName && (
                          <p className="mt-1 text-sm text-red-600">{profileErrors.lastName.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                          Telefon
                        </label>
                        <input
                          {...registerProfile('phone', {
                            maxLength: {
                              value: 20,
                              message: 'Telefon nie może przekraczać 20 znaków'
                            }
                          })}
                          type="tel"
                          className={`block w-full px-3 py-2 border ${
                            profileErrors.phone ? 'border-red-300' : 'border-gray-300'
                          } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                          placeholder="+48 123 456 789"
                        />
                        {profileErrors.phone && (
                          <p className="mt-1 text-sm text-red-600">{profileErrors.phone.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="position" className="block text-sm font-medium text-gray-700">
                          Stanowisko
                        </label>
                        <select
                          {...registerProfile('position')}
                          className={`block w-full px-3 py-2 border ${
                            profileErrors.position ? 'border-red-300' : 'border-gray-300'
                          } rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                        >
                          <option value="">— Wybierz stanowisko —</option>
                          {POSITIONS.map(pos => (
                            <option key={pos} value={pos}>{pos}</option>
                          ))}
                          {user?.position && !POSITIONS.includes(user.position) && (
                            <option value={user.position}>{user.position}</option>
                          )}
                        </select>
                        {profileErrors.position && (
                          <p className="mt-1 text-sm text-red-600">{profileErrors.position.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="hireDate" className="block text-sm font-medium text-gray-700">
                          Data zatrudnienia
                        </label>
                        <input
                          {...registerProfile('hireDate')}
                          type="date"
                          className={`block w-full px-3 py-2 border ${
                            profileErrors.hireDate ? 'border-red-300' : 'border-gray-300'
                          } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                        />
                        {profileErrors.hireDate && (
                          <p className="mt-1 text-sm text-red-600">{profileErrors.hireDate.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="mt-6">
                      <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                        Notatki
                      </label>
                      <textarea
                        {...registerProfile('notes', {
                          maxLength: {
                            value: 1000,
                            message: 'Notatki nie mogą przekraczać 1000 znaków'
                          }
                        })}
                        rows={3}
                        className={`block w-full px-3 py-2 border ${
                          profileErrors.notes ? 'border-red-300' : 'border-gray-300'
                        } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                        placeholder="Dodatkowe informacje..."
                      />
                      {profileErrors.notes && (
                        <p className="mt-1 text-sm text-red-600">{profileErrors.notes.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 px-4 py-3 rounded-md">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Informacje o koncie</h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <p className="text-sm text-gray-500">Rola</p>
                        <p className="text-sm font-medium text-gray-900 capitalize">
                          {user?.role || 'Użytkownik'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Ostatnie logowanie</p>
                        <p className="text-sm font-medium text-gray-900">
                          {user?.lastLogin 
                            ? new Date(user.lastLogin).toLocaleString('pl-PL')
                            : 'Nigdy'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isUpdating}
                      className="btn-primary"
                    >
                      {isUpdating ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Zapisz zmiany
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Zmiana hasła
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Zaktualizuj swoje hasło dla lepszego bezpieczeństwa
                </p>
              </div>
              <div className="card-body">
                <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-6">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                      Obecne hasło
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        {...registerPassword('currentPassword', {
                          required: 'Obecne hasło jest wymagane'
                        })}
                        type="password"
                        className={`block w-full pl-10 pr-3 py-2 border ${
                          passwordErrors.currentPassword ? 'border-red-300' : 'border-gray-300'
                        } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                        placeholder="Obecne hasło"
                      />
                    </div>
                    {passwordErrors.currentPassword && (
                      <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                      Nowe hasło
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Key className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        {...registerPassword('newPassword', {
                          required: 'Nowe hasło jest wymagane',
                          minLength: {
                            value: 6,
                            message: 'Hasło musi mieć co najmniej 6 znaków'
                          },
                          pattern: {
                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                            message: 'Hasło musi zawierać co najmniej jedną małą literę, jedną wielką literę i jedną cyfrę'
                          }
                        })}
                        type="password"
                        className={`block w-full pl-10 pr-3 py-2 border ${
                          passwordErrors.newPassword ? 'border-red-300' : 'border-gray-300'
                        } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                        placeholder="Nowe hasło"
                      />
                    </div>
                    {passwordErrors.newPassword && (
                      <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Potwierdź nowe hasło
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Key className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        {...registerPassword('confirmPassword', {
                          required: 'Potwierdzenie hasła jest wymagane',
                          validate: (value, formValues) => 
                            value === formValues.newPassword || 'Hasła nie są identyczne'
                        })}
                        type="password"
                        className={`block w-full pl-10 pr-3 py-2 border ${
                          passwordErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                        } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                        placeholder="Potwierdź nowe hasło"
                      />
                    </div>
                    {passwordErrors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                    )}
                  </div>

                  <div className="bg-blue-50 px-4 py-3 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <Key className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Wymagania dotyczące hasła
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Co najmniej 6 znaków</li>
                            <li>Przynajmniej jedna mała litera</li>
                            <li>Przynajmniej jedna wielka litera</li>
                            <li>Przynajmniej jedna cyfra</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isChangingPassword}
                      className="btn-primary"
                    >
                      {isChangingPassword ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <>
                          <Key className="h-4 w-4 mr-2" />
                          Zmień hasło
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'email' && user?.role === 'super admin' && (
            <EmailConfig />
          )}

          {activeTab === 'notifications' && user?.role === 'super admin' && (
            <NotificationConfig />
          )}

          {activeTab === 'android' && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Aplikacja Android
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Pobierz aplikację mobilną Farm Connect na urządzenia z systemem Android
                </p>
              </div>
              <div className="card-body">
                <div className="space-y-6">
                  {/* Informacje o aplikacji */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 px-6 py-5 rounded-lg border border-green-200">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <Smartphone className="h-10 w-10 text-green-600" />
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                          Farm Connect Mobile
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Aplikacja mobilna Farm Connect umożliwia dostęp do wszystkich funkcji systemu 
                          bezpośrednio z Twojego urządzenia Android. Zarządzaj urządzeniami, przeglądaj 
                          kamery CCTV i korzystaj z wszystkich funkcji systemu w podróży.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700">
                          <div className="flex items-center">
                            <span className="text-green-600 mr-2">✓</span>
                            <span>Dostęp do kamer CCTV</span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-green-600 mr-2">✓</span>
                            <span>Zarządzanie urządzeniami</span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-green-600 mr-2">✓</span>
                            <span>Powiadomienia push</span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-green-600 mr-2">✓</span>
                            <span>Pełna funkcjonalność</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Przycisk pobierania */}
                  <div className="flex flex-col items-center justify-center py-8 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <Smartphone className="h-16 w-16 text-gray-400 mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      Pobierz aplikację Android
                    </h4>
                    <p className="text-sm text-gray-500 mb-6 text-center max-w-md">
                      Kliknij poniższy przycisk aby pobrać plik APK aplikacji Farm Connect. 
                      Po pobraniu zainstaluj aplikację na swoim urządzeniu Android.
                    </p>
                    
                    <button
                      onClick={handleDownloadApk}
                      disabled={isDownloadingApk}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isDownloadingApk ? (
                        <>
                          <LoadingSpinner size="sm" className="mr-2" />
                          Pobieranie...
                        </>
                      ) : (
                        <>
                          <Download className="h-5 w-5 mr-2" />
                          Pobierz aplikację (APK)
                        </>
                      )}
                    </button>
                    
                    <p className="mt-4 text-xs text-gray-500 text-center max-w-md">
                      Wersja aplikacji: 1.0.0 | Rozmiar: ~25 MB | Wymagania: Android 6.0+
                    </p>
                  </div>

                  {/* Instrukcje instalacji */}
                  <div className="bg-blue-50 px-6 py-5 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                      <Settings className="h-5 w-5 mr-2" />
                      Instrukcja instalacji
                    </h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                      <li>Pobierz plik APK na swoje urządzenie Android</li>
                      <li>Otwórz plik APK (może być wymagane zezwolenie na instalację z nieznanych źródeł)</li>
                      <li>W ustawieniach bezpieczeństwa urządzenia włącz opcję "Instalacja z nieznanych źródeł"</li>
                      <li>Zainstaluj aplikację i zaloguj się używając swoich danych</li>
                      <li>Ciesz się dostępem do Farm Connect z poziomu urządzenia mobilnego!</li>
                    </ol>
                  </div>

                  {/* Uwaga bezpieczeństwa */}
                  <div className="bg-yellow-50 px-6 py-4 rounded-lg border border-yellow-200">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <Settings className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-yellow-800">
                          Uwaga dotycząca bezpieczeństwa
                        </h4>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>
                            Aplikacja jest podpisana cyfrowo i bezpieczna do użycia. 
                            Jeśli Twój system Android ostrzega o instalacji z nieznanych źródeł, 
                            jest to normalne zachowanie - aplikacja nie jest dostępna w Google Play Store.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Komponent zarządzania konfiguracją powiadomień o terminach ogłoszeń
const NotificationConfig = () => {
  const [notificationHours, setNotificationHours] = useState([5, 3, 1]);
  const [notificationMinutes, setNotificationMinutes] = useState([10]);
  const [enabled, setEnabled] = useState(true);
  const [newHour, setNewHour] = useState('');
  const [newMinute, setNewMinute] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const queryClient = useQueryClient();

  // Pobierz konfigurację
  const { data: configData, isLoading } = useQuery(
    'announcementNotificationConfig',
    async () => {
      const response = await api.get('/announcements/notification-config');
      return response.data.config;
    },
    {
      onSuccess: (data) => {
        if (data) {
          setNotificationHours(data.notificationHours || [5, 3, 1]);
          setNotificationMinutes(data.notificationMinutes || [10]);
          setEnabled(data.enabled !== false);
        }
      }
    }
  );

  // Mutacja do zapisania konfiguracji
  const saveMutation = useMutation(
    async (config) => {
      const response = await api.put('/announcements/notification-config', config);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Konfiguracja została zapisana');
        queryClient.invalidateQueries('announcementNotificationConfig');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas zapisywania konfiguracji');
      }
    }
  );

  // Mutacja do ręcznego sprawdzenia terminów
  const checkMutation = useMutation(
    async () => {
      const response = await api.post('/announcements/check-deadlines');
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(`Sprawdzono terminy: ${data.notificationsSent} powiadomień, ${data.emailsSent} emaili wysłanych`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas sprawdzania terminów');
      }
    }
  );

  const handleAddHour = () => {
    const hour = parseInt(newHour);
    if (hour > 0 && !notificationHours.includes(hour)) {
      setNotificationHours([...notificationHours, hour].sort((a, b) => b - a));
      setNewHour('');
    }
  };

  const handleRemoveHour = (hour) => {
    setNotificationHours(notificationHours.filter(h => h !== hour));
  };

  const handleAddMinute = () => {
    const minute = parseInt(newMinute);
    if (minute > 0 && minute < 60 && !notificationMinutes.includes(minute)) {
      setNotificationMinutes([...notificationMinutes, minute].sort((a, b) => b - a));
      setNewMinute('');
    }
  };

  const handleRemoveMinute = (minute) => {
    setNotificationMinutes(notificationMinutes.filter(m => m !== minute));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveMutation.mutateAsync({
        notificationHours,
        notificationMinutes,
        enabled
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCheck = async () => {
    setIsChecking(true);
    try {
      await checkMutation.mutateAsync();
    } finally {
      setIsChecking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Konfiguracja powiadomień o terminach ogłoszeń
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Zarządzaj godzinami wysyłki powiadomień o zbliżających się terminach wydarzeń
        </p>
      </div>
      <div className="card-body space-y-6">
        {/* Włącz/Wyłącz powiadomienia */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">
              Włącz automatyczne powiadomienia o terminach
            </span>
          </label>
          <p className="mt-1 text-xs text-gray-500">
            Powiadomienia są sprawdzane automatycznie co 1 minutę
          </p>
        </div>

        {/* Godziny */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Powiadomienia w godzinach przed terminem
          </label>
          <div className="space-y-2">
            {notificationHours.map((hour) => (
              <div key={hour} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                <span className="text-sm text-gray-700">
                  {hour} {hour === 1 ? 'godzinę' : hour < 5 ? 'godziny' : 'godzin'} przed terminem
                </span>
                <button
                  onClick={() => handleRemoveHour(hour)}
                  className="text-red-600 hover:text-red-800"
                  type="button"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                value={newHour}
                onChange={(e) => setNewHour(e.target.value)}
                placeholder="Dodaj godzinę"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                onClick={handleAddHour}
                disabled={!newHour || notificationHours.includes(parseInt(newHour))}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                type="button"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Minuty */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Powiadomienia w minutach przed terminem
          </label>
          <div className="space-y-2">
            {notificationMinutes.map((minute) => (
              <div key={minute} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                <span className="text-sm text-gray-700">
                  {minute} {minute === 1 ? 'minutę' : minute < 5 ? 'minuty' : 'minut'} przed terminem
                </span>
                <button
                  onClick={() => handleRemoveMinute(minute)}
                  className="text-red-600 hover:text-red-800"
                  type="button"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                max="59"
                value={newMinute}
                onChange={(e) => setNewMinute(e.target.value)}
                placeholder="Dodaj minuty (1-59)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                onClick={handleAddMinute}
                disabled={!newMinute || notificationMinutes.includes(parseInt(newMinute))}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                type="button"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Informacja */}
        <div className="bg-blue-50 px-4 py-3 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <Bell className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Jak działają powiadomienia?
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  System automatycznie sprawdza ogłoszenia z terminami wydarzeń i wysyła powiadomienia 
                  (w systemie i emaile) użytkownikom w określonych momentach przed terminem.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Przyciski akcji */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <button
            onClick={handleCheck}
            disabled={isChecking}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            type="button"
          >
            {isChecking ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sprawdź terminy teraz
              </>
            )}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary"
            type="button"
          >
            {isSaving ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Zapisz konfigurację
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
